/**
 * The learner app has no AI-credit menu, so — unlike admin-web, where these types live beside the
 * profile-menu hook that reads them — they are declared here, next to their only consumer.
 */
export type AiCreditWindow = {
  usedCredits: number;
  poolCredits: number;
  /**
   * Whether this window is refusing requests right now. It comes from the server because the gate
   * refuses on a strict `used > pool`, so deriving it here would report someone as blocked at exactly
   * 10.000/10.000 while their next request still goes through.
   */
  blocked: boolean;
  /** Epoch ms when the window RESETS; null unless it is actually blocked. */
  freesAt: number | null;
};

export type AiCreditStatus = {
  /** ADMIN and the internal caller are never refused on budget, and have no pool to show. */
  exempt: boolean;
  role?: string | null;
  hourly?: AiCreditWindow | null;
  weekly?: AiCreditWindow | null;
};

/** Which of the two anchored windows is the one holding the user back. */
export type AiCreditWindowName = "hourly" | "weekly";

export type AiCreditLimitReset = {
  /** Epoch ms the pool comes back, or null when no window claims to be blocked. */
  freesAt: number | null;
  window: AiCreditWindowName | null;
};

const NO_RESET: AiCreditLimitReset = { freesAt: null, window: null };

const resolveBlockedFreesAt = (
  creditWindow: AiCreditWindow | null | undefined,
  now: number,
): number | null => {
  if (!creditWindow || !creditWindow.blocked) return null;
  const freesAt = creditWindow.freesAt;
  if (typeof freesAt !== "number" || !Number.isFinite(freesAt)) return null;
  // A moment that has already passed is worse than no moment at all — see the note on
  // `buildAiCreditRowView`, which refuses the same thing for the same reason.
  return freesAt > now ? freesAt : null;
};

/**
 * When the AI pool that just refused a request comes back.
 *
 * The 423 itself cannot say: its body is the feature's error key, and the backend sends no `Retry-After`.
 * The credit-status endpoint is the only thing that knows, and it reports BOTH windows — so the caller has
 * to pick, and the answer is not "whichever is fuller".
 *
 * **Hourly wins when both are blocked.** They are the same pool seen through two anchored windows, so
 * being over the hourly cap is the shorter wait and the one that actually clears; announcing next Monday
 * when the block lifts at the top of the hour is the difference between "wait a bit" and "give up".
 *
 * **Neither blocked is a real, reachable answer, not a bug.** The pool can free between the refusal and
 * this read, and an exempt caller (ADMIN, the internal client) sends no windows at all. The caller then
 * still has a refusal to explain but no time to offer, which is why this returns a null `freesAt` rather
 * than throwing or guessing.
 */
export const resolveLimitFreesAt = (
  status: AiCreditStatus | null | undefined,
  now: number = Date.now(),
): AiCreditLimitReset => {
  if (!status || status.exempt) return NO_RESET;
  const hourlyFreesAt = resolveBlockedFreesAt(status.hourly, now);
  if (hourlyFreesAt !== null) return { freesAt: hourlyFreesAt, window: "hourly" };
  const weeklyFreesAt = resolveBlockedFreesAt(status.weekly, now);
  if (weeklyFreesAt !== null) return { freesAt: weeklyFreesAt, window: "weekly" };
  return NO_RESET;
};
